import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { qualifyUrl } from "util/URLUtils";
import fetch from "logic/rest/FetchProvider";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { EventDefinitionsStore } from "stores/event-definitions/EventDefinitionsStore";
import { EventNotificationsStore } from "stores/event-notifications/EventNotificationsStore";
import { defaultOnError } from "util/conditional/onError";
import FiltersForQueryParams from "components/common/EntityFilters/FiltersForQueryParams";
import { CurrentUserStore } from "stores/users/CurrentUserStore";
import EventDefinitionTagsFilter from "components/event-definitions/EventDefinitionTagsFilter";
const TAGS_ATTRIBUTE = {
  id: "tags",
  title: "Tags",
  type: "STRING",
  sortable: false,
  searchable: true,
  filterable: true,
  filter_component: EventDefinitionTagsFilter
};
export const fetchEventDefinitions = (searchParams) => {
  CurrentUserStore.update(CurrentUserStore.getInitialState().currentUser.username);
  return EventDefinitionsStore.searchPaginated(searchParams.page, searchParams.pageSize, searchParams.query, {
    sort: searchParams == null ? void 0 : searchParams.sort.attributeId,
    order: searchParams == null ? void 0 : searchParams.sort.direction,
    filters: FiltersForQueryParams(searchParams.filters)
  }).then(({ elements, pagination, attributes }) => ({
    list: elements,
    pagination,
    attributes: [...attributes != null ? attributes : [], TAGS_ATTRIBUTE]
  }));
};
export const fetchEventDefinition = (eventDefinitionId) => EventDefinitionsStore.get(eventDefinitionId).then(({ event_definition, context, is_mutable }) => ({
  eventDefinition: event_definition,
  context,
  is_mutable
}));
export const keyFn = (searchParams) => ["eventDefinition", "overview", searchParams];
export const useGetEventDefinition = (eventDefinitionId) => {
  const { data, isFetching } = useQuery({
    queryKey: ["get-event-definition", eventDefinitionId],
    queryFn: () => defaultOnError(
      fetchEventDefinition(eventDefinitionId),
      "Loading Event Definition failed with status",
      "Could not load Event definition"
    )
  });
  return {
    data: isFetching ? null : data,
    isFetching
  };
};
const useEventDefinitions = (searchParams, { enabled } = { enabled: true }) => {
  const { data, refetch, isInitialLoading } = useQuery({
    queryKey: keyFn(searchParams),
    queryFn: () => defaultOnError(
      fetchEventDefinitions(searchParams),
      "Loading Event Definitions failed with status",
      "Could not load Event definition"
    ),
    placeholderData: keepPreviousData,
    enabled
  });
  return {
    data,
    refetch,
    isInitialLoading
  };
};
export default useEventDefinitions;
export function useGetEntityTypes() {
  const { data, isLoading } = useQuery({
    queryKey: ["event-definitions", "entity-types"],
    queryFn: () => defaultOnError(
      fetch("GET", qualifyUrl("/events/entity_types")),
      "Loading event definition entity types failed with status",
      "Could not load event definition entity types"
    )
  });
  return {
    entityTypes: isLoading ? [] : data || [],
    loadingEntityTypes: isLoading
  };
}
export function useGetListEventsClusterConfig() {
  const { data, isLoading } = useQuery({
    queryKey: ["event-definitions", "list-events-cluster-config"],
    queryFn: () => defaultOnError(
      ConfigurationsActions.listEventsClusterConfig(),
      "Loading event definition list events cluster config failed with status",
      "Could not load event definition list events cluster config"
    )
  });
  return {
    eventsClusterConfig: isLoading ? {} : data || {},
    loadingEventsClusterConfig: isLoading
  };
}
export function useGetEventNotifications() {
  const { data, isLoading } = useQuery({
    queryKey: ["event-definitions", "event-notifications"],
    queryFn: () => defaultOnError(
      EventNotificationsStore.listAll(),
      "Loading event notifications failed with status",
      "Could not load event notifications"
    )
  });
  return {
    eventNotifications: { all: isLoading ? [] : (data == null ? void 0 : data.notifications) || [] },
    loadingEventNotifications: isLoading
  };
}
